import React, { useMemo, useState, useEffect } from "react";
import { connect } from "react-redux";
import { getFormValues, getFormSyncErrors, change } from "redux-form";
import { withTranslation } from "react-i18next";
import DynamicForm from "../../common/DynamicForm";
import { GET_MAX_FILE_SIZE_FROM_DICT } from "constants/index";
import baseService from "services/BaseService";
import { getModalFormDataFromState } from "helpers";
import { getMergedStaticBlocks } from "./config";

const STATIC_FORM_NAME = "object-modal-form";

const FALLBACK_OBJECT_TYPE = "CUSTOMER";

const mapStateToProps = (state, ownProps) => {
    const objectFormName = ownProps.customFormName ? ownProps.customFormName : STATIC_FORM_NAME;
    const modalFormData = getModalFormDataFromState(ownProps.staticBlockFormKey, state);
    return {
        objectFormName,
        formErrors: getFormSyncErrors(objectFormName)(state),
        formValues: getFormValues(objectFormName)(state),
        modalFormData: modalFormData && modalFormData.objectData,
    };
};

const mapDispatchToProps = { change };

const ObjectForm = props => {
    const {
        t,
        disabled = false,
        customerId,
        customerObjectType,
        formErrors,
        formValues,
        change,
        modalFormData,
        objectFormName,
    } = props;

    const objectType = customerObjectType || FALLBACK_OBJECT_TYPE;

    const [isApartmentsLoading, setIsApartmentsLoading] = useState(false);
    const [apartments, setApartments] = useState([]);

    const maxFileSize = useMemo(() => GET_MAX_FILE_SIZE_FROM_DICT("form"), []);

    const cityQuery = useMemo(() => {
        return formValues && formValues.city;
    }, [formValues]);

    const siteId = useMemo(() => {
        return formValues && formValues.object && formValues.object.value;
    }, [formValues]);

    const initObjectForm = useMemo(() => {
        const widgets = [];

        const restWidgets = [
            {
                key: "city",
                title: t("city"),
                searchable: true,
                customAjaxKey: "object_search",
                customAjaxMethod: "post",
                customAjaxParams: query => ({
                    data: { filter: [{ query, field: "city" }], type: "city" },
                    jsonType: true,
                }),
                customAjaxOptionsConverter: result => {
                    return result.map(item => {
                        return { key: item.id, value: item.objectName };
                    });
                },
                hasExValue: true,
                widgetType: "ajaxCombo",
            },
            {
                key: "object",
                title: t("address"),
                searchable: true,
                values: [],
                isReq: true,
                customAjaxKey: "object_search",
                customAjaxMethod: "post",
                customAjaxParams: query => ({
                    data: {
                        filter: [
                            { query: cityQuery ? cityQuery.label : "", field: "city" },
                            { query, field: "site" },
                        ],
                        type: "site",
                    },
                    jsonType: true,
                }),
                customAjaxOptionsConverter: result => {
                    return result.map(item => {
                        return { key: item.id, value: item.objectName };
                    });
                },
                hasExValue: true,
                widgetType: "ajaxCombo",
            },
            {
                key: "apartment",
                title: t("apartment"),
                maxLength: 32,
                disabled: isApartmentsLoading,
                values: apartments,
                isReq: true,
                isCreatable: true,
                hasExValue: true,
                widgetType: "select",
            },
        ];

        const concatWidgets = widgets.concat(restWidgets);
        const mergedWidgetsWithStaticConfig = modalFormData
            ? getMergedStaticBlocks(t, modalFormData, concatWidgets)
            : concatWidgets;
        return [
            {
                view: "1column",
                title: t(modalFormData.translationLabel),
                collapsed: false,
                key: "customer-new-address",
                widgets: mergedWidgetsWithStaticConfig,
            },
        ];
    }, [cityQuery, apartments, isApartmentsLoading]);

    async function fetchApartments(siteId) {
        setIsApartmentsLoading(true);
        try {
            const response = await baseService.get("apart_search", {
                data: { siteId },
            });
            if (response && response.success) {
                setApartments(response.result ? response.result.values : []);
            }
        } catch (e) {
            console.error("ObjectForm::fetchApartments: Error", e);
        } finally {
            setIsApartmentsLoading(false);
        }
    }

    // async function fetchZipCodes(streetId, buildId) {
    //     setIsZipCodesLoading(true);
    //     try {
    //         const response = await baseService.get("search_zip_code", {
    //             data: { streetId, buildId },
    //         });
    //         if (response && response.success) {
    //             setZipCodes(response.result);
    //         }
    //     } catch (e) {
    //         console.error("ObjectForm::fetchZipCodes: Error", e);
    //     } finally {
    //         setIsZipCodesLoading(false);
    //     }
    // }

    function handleResetOnFieldChange(event, newValue, previousValue, name) {
        if (name === "appartment") {
            change(objectFormName, "appartmentCreatableInput", "");
        }
    }

    useEffect(() => {
        if (siteId) {
            fetchApartments(siteId);
        }
    }, [siteId]);

    // useEffect(() => {
    //     if (buildId && streetMode === StreetModes.AUTO) {
    //         if (buildId) {
    //             fetchZipCodes(streetId, buildId);
    //         } else {
    //             setZipCodes([]);
    //         }
    //     }
    // }, [buildId, streetMode]);

    return (
        <DynamicForm
            t={t}
            formName={objectFormName}
            dynData={initObjectForm}
            objectId={customerId}
            currentId={customerId}
            objectType={objectType}
            maxFileSize={maxFileSize}
            onFieldChange={handleResetOnFieldChange}
            withoutLiveSaver
        />
    );
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(ObjectForm));
